<script lang="ts">
    import { browser } from '$app/environment';
    import { onMount } from 'svelte';
    import { goto } from '$app/navigation';
    import { page } from '$app/stores';
    import { onDestroy } from 'svelte';
    import { supabase } from '$lib/supabase';
    import { _, locale } from 'svelte-i18n';
    import { formatRelativeTime } from '$lib/utils/i18nDateUtils';
    import { hasTranslation, t } from '$lib/i18n';
    
    // shadcn/svelte
    import { cn } from "$lib/components/shadcn/utils";
    import { Button } from "$lib/components/shadcn/ui/button";
    import { Badge } from "$lib/components/shadcn/ui/badge";
    import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "$lib/components/shadcn/ui/card";
    import { ScrollArea } from "$lib/components/shadcn/ui/scroll-area";
    import { Separator } from "$lib/components/shadcn/ui/separator";
    import { Input } from "$lib/components/shadcn/ui/input";
    import { Collapsible } from "$lib/components/shadcn/ui/collapsible";
    import MobileNavigation from "$lib/components/video-mobile/MobileNavigation.svelte";
    import MobileHeader from "$lib/components/video-mobile/MobileHeader.svelte";
    import MobileContent from "$lib/components/video-mobile/MobileContent.svelte";
    import MobileRanking from "$lib/components/video-mobile/MobileRanking.svelte";
    
    import { 
        Heart,
        Eye, 
        Calendar, 
        User, 
        ArrowLeft, 
        ChevronDown, 
        ChevronUp, 
        Info,
        MessageSquare,
        Youtube,
        Share2,
        Twitter,
        MessageCircle,
        Users,
        Grid,
        BarChart2,
        X,
        Search,
        RefreshCw,
        Maximize,
        Link,
        ExternalLink,
        MoreVertical,
        PlayCircle
    } from 'lucide-svelte';
    import LoginPopup from '$lib/components/LoginPopup.svelte';
    import { authStore } from '$lib/stores/authStore';
    import { tweened } from 'svelte/motion';
    import { cubicOut } from 'svelte/easing';
    import { getMaxConcurrentViewers } from '$lib/utils/statsUtils';
    import { formatNumber } from '$lib/utils/numberUtils';
    import { updateMiniPlayer, currentVideoId } from '$lib/stores/miniPlayerStore';
    import { formatDate } from '$lib/utils/dateUtils';
    import { writable } from 'svelte/store';
    import { flip } from 'svelte/animate';
    import { quintOut } from 'svelte/easing';
    import { setAuthState } from '$lib/stores/authStore';
    import { favoriteStore } from '$lib/stores/favoriteStore';
    import { liveRankingStore } from '$lib/stores/liveRankingStore';
    import { followStore, initializeFollowData } from '$lib/stores/followStore';
    import { fade, slide } from 'svelte/transition';
    import ShareButton from '$lib/components/ShareButton.svelte';
    import Chart from 'chart.js/auto';
    import VideoPlayer from '$lib/components/video/VideoPlayer.svelte';
    import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "$lib/components/shadcn/ui/dialog";
    import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "$lib/components/shadcn/ui/dropdown-menu";
    
    // ビデオプレーヤーコンポーネントへの参照
    let videoPlayerComponent: any;
    
    // メインページのiframeElement参照を保持（アーカイブチャット用）
    let iframeElement: HTMLIFrameElement | null = null;
    let lastSyncTime = 0;

    export let data;

    // 型定義
    interface VideoDetails {
        id: string;
        title: string;
        channel_id: string;
        channel_title: string;
        description?: string;
        thumbnail_url: string;
        live_broadcast_content: 'live' | 'upcoming' | 'none';
        published_at: string;
        last_live_view_count?: number;
        platform: 'youtube' | 'twitch';
        scheduled_start_time?: string;
        duration?: string;
        live_stats?: Record<string, number>;
        concurrentViewers?: number;
        videoStats?: {
            totalSuperChatJpy: number;
            totalCommentCount: number;
        };
    }

    // Constants
    const MAX_VISIBLE_PARAGRAPHS = 2;
    const MODES = {
        CHAT: 'chat',
        DETAILS: 'details',
        RECENT: 'recent'
    };

    // State
    let videoDetails: any = null;  // 明示的に初期化
    let relatedVideos: any[];
    let channelData: any;
    let currentMode = MODES.DETAILS; // 初期値を設定
    let chatUrl: string;
    let embedDomain = browser ? window.location.hostname : '';
    let isDescriptionExpanded = false;
    let paragraphs: string[] = [];
    let showGraph = false;
    let viewerChart: Chart | null = null;
    let chartCanvas: HTMLCanvasElement;
    let showMobileMenu = false; // モバイルメニューの表示状態

    let isMobile: boolean;
    let headerHeight: number;
    let videoHeight: number;
    let chatHeight: number;
    let videoContainer: HTMLElement;
    let buttonSize = 40;
    let rippleSize = buttonSize * 3;
    let showMoreOptions = false;
    let isRippling = false;
    let rippleTimeoutId: number | null = null;
    let contentContainer: HTMLElement;
    let startTime: Date;
    let timeUntilLive = '';
    let isFavorite = false;
    let showLoginPopup = false;
    let concurrentViewers = videoDetails?.concurrentViewers || 0;
    let totalSuperChatJpy = videoDetails?.videoStats?.totalSuperChatJpy || 0;
    let totalCommentCount = videoDetails?.videoStats?.totalCommentCount || 0;
    let isRankingVisible = true;
    let selectedVideoButton: HTMLElement;
    let groups = data.groups;
    let selectedGroup = '';
    let followedLiveStreams = [];
    let searchKeyword = '';
    let selectedBoard = "https://bbs.jpnkn.com/nijifunbbs/beta/fast";
    let showCommentViewer = false;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let popupWindow: Window | null = null;
    let showSidebar = true; // デフォルトでは非表示
    let showChat = true; // チャットの表示/非表示
    let currentLayout = 'desktop'; // desktop または mobile
    let isLiveListCollapsed = false; // サイドバーの配信リストの折りたたみ状態
    let isLiveChatCollapsed = false; // チャットの折りたたみ状態（内容のみ折りたたみ、ヘッダーは残す）
    
    // Holodex-Plus拡張機能の検出フラグ
    let extensionDetected = false;
    let archiveChatUrl = '';
    let videoTime = 0;  // 動画の再生位置（秒）
    let videoTimeMs = 0; // 動画の再生位置（ミリ秒）
    let mobileChatIframe: HTMLIFrameElement | null = null;
    let lastSeekTime = 0; // 最後にチャットURLを更新した時間
    let playerTimeTracker: ReturnType<typeof setInterval> | null = null;
    let initialSeekComplete = false; // 初期シークが完了したかどうか

    // イベント情報の状態
    let eventInfo: any = data?.eventInfo || null;
    let isEventCollapsed = false;

    // イベント情報で使用する関数
    function getVideoType(video: any): 'live' | 'upcoming' | 'archived' {
        if (!video.live_broadcast_content || video.live_broadcast_content === 'none') {
            return 'archived';
        }
        if (video.live_broadcast_content === 'live') {
            return 'live';
        }
        if (video.live_broadcast_content === 'upcoming') {
            return 'upcoming';
        }
        return 'archived';
    }

    // チャットの再生位置を同期する関数
    function syncChatPosition(iframe: HTMLIFrameElement | null, seekSec: number) {
        console.log('syncChatPosition:', seekSec);
        if (!iframe) {
            console.error('syncChatPosition: iframe が存在しません');
            return;
        }
        if (!iframe || !iframe.contentWindow) {
            console.error('syncChatPosition: iframe または contentWindow が存在しません');
            return;
        }
        
        try {
            // 秒からミリ秒に変換
            const seekMs = Math.floor(seekSec * 1000);
            console.log('チャット再生位置を同期します:', seekSec, '秒 (', seekMs, 'ミリ秒)');
            
            // 複数の形式で同期メッセージを送信して互換性を確保
            
            // 1. 標準形式
            iframe.contentWindow.postMessage({
                action: 'syncTime',
                seekMs: seekMs
            }, '*');
            
            // 2. YouTubeプレーヤーの形式
            iframe.contentWindow.postMessage({
                "yt-player-video-progress": seekSec
            }, "*");
            
            // 3. カスタム形式（冗長だが互換性のため）
            iframe.contentWindow.postMessage({
                type: "videoTimeUpdate",
                currentTime: seekSec
            }, "*");
            
            // 4. 単純な形式
            iframe.contentWindow.postMessage({
                videoCurrentTime: seekSec,
                videoCurrentTimeMs: seekMs
            }, "*");
            
            // 大きな時間変更の場合のみcontinuationトークンを更新
            const timeDiffSec = Math.abs(seekSec - lastSeekTime);
            if (timeDiffSec > 30) { // 30秒以上の変化で更新
                console.log('大きな時間変更があったため、新しい継続トークンを取得します:', timeDiffSec, '秒の差');
                
                // 新しい継続トークンを取得して適用（iframeは再読み込みせず）
                updateContinuationToken(seekMs);
                
                // 最後の同期時間を更新
                lastSeekTime = seekSec;
            }
        } catch (e) {
            console.error('チャット再生位置同期中にエラー:', e);
        }
    }

    // チャットの再生位置を同期する関数（VideoPlayerからのイベントで呼び出される）
    function handleChatSync(event: CustomEvent) {
        console.log('handleChatSync:', event.detail);
        const { time } = event.detail;
        if (iframeElement) {
            syncChatPosition(iframeElement, time);
        }
    }

    // VideoPlayerの大きな時間変更イベントを処理
    function handleSignificantTimeChange(event: CustomEvent) {
        const { time, timeMs } = event.detail;
        
        console.log('大きな時間変更を検出:', time, '秒、チャット同期を開始します');
        
        // アーカイブチャットの場合のみチャット位置を同期（アーカイブかどうかに関わらず同期を試みる）
        if (videoDetails && videoDetails.live_broadcast_content !== 'live') {
            // iframeElementがないか確認し、ログに出力
            if (!iframeElement) {
                console.error('iframeElementが見つかりません。チャット同期ができません。');
                
                // iframeElementを再取得
                iframeElement = document.querySelector('iframe[title="YouTube Archive Chat"]');
                console.log('iframeElement再取得:', iframeElement ? '成功' : '失敗');
            }
            
            if (iframeElement) {
                console.log('アーカイブチャットを同期します:', time, '秒');
                syncChatPosition(iframeElement, time);
                lastSyncTime = time;
            }
        }
    }


    onMount(() => {
        showSidebar = window.innerWidth >= 1024; // PC表示の場合はサイドバーを表示
        
        // レイアウトを初期化
        handleResize();
        window.addEventListener('resize', handleResize);
        
        // お気に入りボタンのカスタムイベントを処理
        window.addEventListener('favoriteClick', ((event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail && customEvent.detail.videoId) {
                handleFavoriteClick(new MouseEvent('click'));
            }
        }) as EventListener);

        // ランキング表示のカスタムイベントを処理
        window.addEventListener('rankingToggle', ((event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail) {
                isRankingVisible = customEvent.detail.isVisible;
                localStorage.setItem('rankingVisible', isRankingVisible.toString());
            }
        }) as EventListener);
        
        // YouTubeアーカイブチャットからのメッセージを受け取るためのハンドラを設定
        window.addEventListener('message', (event) => {
            try {
                // チャットフレームからのメッセージを確認
                if (event.data && typeof event.data === 'object') {
                    if (event.data.type === 'CHAT_READY') {
                        console.log('チャットフレームの準備完了メッセージを受信');
                        
                        // チャットフレームの準備ができたら現在の時間を同期
                        if (iframeElement && iframeElement.contentWindow) {
                            // 現在の動画再生位置も同期
                            setTimeout(() => {
                                if (iframeElement && iframeElement.contentWindow) {
                                    iframeElement.contentWindow.postMessage({ "yt-player-video-progress": videoTime }, "*");
                                }
                            }, 1000);
                        }
                    } else if (event.data.type === 'CHAT_ERROR') {
                        console.error('チャットフレームからエラーを受信:', event.data.error);
                        
                        // エラーが発生したら、URLを再構築して再読み込み
                        if (videoDetails && videoDetails.id) {
                            console.log('エラーが発生したため、URLを再構築します');
                            
                            // 少し待ってから初期化処理を実行
                            setTimeout(() => {
                                initializeArchiveChat(); // 新しい方法で初期化
                            }, 2000);
                        }
                    }
                }
            } catch (e) {
                console.error('メッセージ処理中にエラー:', e);
            }
        });
        
        // モバイルメニューを閉じるためのクリックイベントリスナーを追加
        const handleClickOutside = (event: MouseEvent) => {
            // メニューが開いていて、クリックされた要素がメニュー内でない場合に閉じる
            const target = event.target as HTMLElement;
            if (showMobileMenu && 
                !target.closest('.mobile-menu') && 
                !target.closest('button[on\\:click*="showMobileMenu"]')) {
                showMobileMenu = false;
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        
        // Holodex-Plus拡張機能の検出
        if (browser && window && videoDetails.live_broadcast_content !== 'live') {
            console.log('拡張機能検出: チェック開始');
            // @ts-ignore - カスタムプロパティ
            console.log('VTGRID_PLUS_INSTALLED:', window.VTGRID_PLUS_INSTALLED);
            // @ts-ignore - カスタムプロパティ
            console.log('HOLODEX_PLUS_INSTALLED:', window.HOLODEX_PLUS_INSTALLED);
            console.log('window.location:', window.location.href);
            // @ts-ignore - カスタムプロパティ
            if (window.VTGRID_PLUS_INSTALLED) {
                console.log('Holodex-Plus拡張機能を検出しました');
                extensionDetected = true;
                
                // アーカイブ動画の場合、YouTubeのコメント表示URLを設定
                if (videoDetails && videoDetails.platform === 'youtube' && videoDetails.live_broadcast_content !== 'live') {
                    console.log('アーカイブ動画を検出:', videoDetails.id);
                    console.log('embedDomain:', embedDomain);
                    
                    // 新しい方法でアーカイブチャットを初期化
                    initializeArchiveChat();
                } else {
                    console.log('アーカイブ動画ではありません:', videoDetails?.live_broadcast_content);
                }
            } else {
                console.log('拡張機能が検出されませんでした');
            }
        }
    });

    // Streamerグループの取得
    let streamerGroup = groups?.find(group => group.name === 'Streamer');
    let streamerGroupId = streamerGroup?.id;

    $: filteredLiveRankings = selectedGroup === 'followed'
        ? $liveRankingStore.filter(video => 
            $followStore.followedChannels.includes(video.channel_id) &&
            (searchKeyword === '' || 
             video.title.toLowerCase().includes(searchKeyword.toLowerCase()) || 
             video.channel_title.toLowerCase().includes(searchKeyword.toLowerCase())) &&
             // Streamerグループを除外
            (!streamerGroupId || 
             !data.groupChannels?.some(c => c.id === video.channel_id && c.group_id === streamerGroupId))
          )
        : selectedGroup
            ? $liveRankingStore.filter(video => {
                const channel = data.groupChannels?.find(channel => 
                    channel.id === video.channel_id && 
                    channel.group_id === selectedGroup
                );
                // 選択したグループの動画のみを表示する
                // もし選択したグループがStreamerグループの場合は表示する
                return channel && 
                       (searchKeyword === '' || 
                        video.title.toLowerCase().includes(searchKeyword.toLowerCase()) || 
                        video.channel_title.toLowerCase().includes(searchKeyword.toLowerCase()));
              })
            : $liveRankingStore.filter(video => 
                (searchKeyword === '' || 
                 video.title.toLowerCase().includes(searchKeyword.toLowerCase()) || 
                 video.channel_title.toLowerCase().includes(searchKeyword.toLowerCase())) &&
                // Streamerグループを除外
                (!streamerGroupId || 
                 !data.groupChannels?.some(c => c.id === video.channel_id && c.group_id === streamerGroupId))
              );

    $: {
        if ($authStore.user && videoDetails?.id) {  // videoDetailsの存在確認を追加
            favoriteStore.initializeFavorites(videoDetails.id);
        } else {
            isFavorite = false;
        }
    }

    // お気に入り状態の監視
    $: isFavorite = $favoriteStore.favorites.includes(videoDetails?.id);

    // フォロー中の配信数を計算
    $: followedLiveStreams = $liveRankingStore.filter(video => 
        $followStore.followedChannels.includes(video.channel_id)
    );

    // Reactive
    $: {
        if ($page.params.id) {
            videoDetails = data.videoDetails;
            relatedVideos = data.relatedVideos;
            channelData = data.channelData;
            fetchLiveViewers();

            if (browser) {
                // setTimeout(scrollToSelectedVideo, 300);
                
                try {
                    const videoId = videoDetails.id;
                    console.log('Saving video to history on change, ID:', videoId);
                    
                    let history: string[] = [];
                    const storedHistory = localStorage.getItem('recentlyWatchedVideos');
                    
                    if (storedHistory) {
                        history = JSON.parse(storedHistory);
                    }
                    
                    history = history.filter(id => id !== videoId);
                    
                    history.unshift(videoId);
                    
                    // if (history.length > 10) {
                    //     history = history.slice(0, 10);
                    // }
                    
                    localStorage.setItem('recentlyWatchedVideos', JSON.stringify(history));
                    console.log('History saved to localStorage');
                } catch (error) {
                    console.error('Failed to save video to history:', error);
                }
            }
            // updateMiniPlayer(videoDetails.id, true, videoDetails.platform, channelData?.twitch_id);

        
            // ペャットURLの設定
            if (videoDetails.platform === 'youtube') {
                chatUrl = `https://www.youtube.com/live_chat?v=${videoDetails.id}&embed_domain=${embedDomain}`;
            } else if (videoDetails.platform === 'twitch' && channelData?.twitch_id) {
                chatUrl = `https://www.twitch.tv/embed/${channelData.twitch_id}/chat?parent=${embedDomain}&darkpopout`;
            }
            
            if (browser) {
                scrollToTop();
            }

            // MiniPlayerの状態を更新
            if ($currentVideoId !== videoDetails.id) {
                updateMiniPlayer(videoDetails.id, true, videoDetails.platform, channelData?.twitch_id);
            }
        }
    }

    $: {
        if (videoDetails && videoDetails.description) {
            paragraphs = splitIntoParagraphs(videoDetails.description);
        } else {
            paragraphs = [];
        }

        if (videoDetails) {
            const isLiveStream = videoDetails.live_broadcast_content === 'live';
            currentMode = isLiveStream ? MODES.CHAT : MODES.DETAILS;
        }
    }

    $: visibleParagraphs = isDescriptionExpanded ? paragraphs : paragraphs.slice(0, MAX_VISIBLE_PARAGRAPHS);
    $: pageTitle = generateTitle(videoDetails);
    $: pageDescription = generateDescription(videoDetails);
    $: maxConcurrentViewers = getMaxConcurrentViewers(videoDetails?.live_stats);

    // Lifecycle
    onMount(async () => {
        initializeLayout();
        initializeElapsedTime();
        initializeTimeUntilLive();
        initializeLiveViewers();
        // initializeLiveChatHistory();
        initializeRankingState();
        initializeLiveRankings();
        initializeFollowData();
        if (isMobile) {
            // updateMiniPlayer(videoDetails.id, true, videoDetails.platform, channelData?.twitch_id);
        }
        scrollToTop();
        // setTimeout(scrollToSelectedVideo, 100);
        if ($authStore.user) {
            await favoriteStore.initializeFavorites(videoDetails.id);
        }

        // 動画IDのみをローカルストレージに保存（視聴履歴）
        try {
          if (browser) {
            const videoId = videoDetails.id;
            
            let history: string[] = [];
            const storedHistory = localStorage.getItem('recentlyWatchedVideos');
            
            if (storedHistory) {
              history = JSON.parse(storedHistory);
            }
            
            history = history.filter(id => id !== videoId);
            
            history.unshift(videoId);
            
            // if (history.length > 10) {
            //     history = history.slice(0, 10);
            // }
            
            localStorage.setItem('recentlyWatchedVideos', JSON.stringify(history));
          }
        } catch (error) {
          console.error('Failed to save video to history:', error);
        }

        // グラフの表示状態を監視
        if (showGraph && chartCanvas && videoDetails?.viewerStats) {
            initializeChart();
        }
    });

    function updateSafeArea() {
        if (typeof window !== 'undefined') {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        }
    }

    onMount(() => {
        updateSafeArea();
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateSafeArea);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateSafeArea);
            }
        };
    });

    onMount(() => {
        if (browser) {
            handleResize(); // 初期化
            window.addEventListener('resize', handleResize);
        }
        
        // その他の初期化コード...
        return () => {
            if (browser) {
                window.removeEventListener('resize', handleResize);
            }
        };
    });

    // Methods
    function initializeLayout() {
        if (!browser) return;

        const updateLayout = () => {
            isMobile = window.innerWidth < 1024;
            headerHeight = document.querySelector('header')?.offsetHeight || 0;
            videoHeight = videoContainer?.offsetHeight || 0;
            if (!isMobile) {
                chatHeight = videoHeight - 120;
            } else {
                const windowHeight = window.innerHeight;
                const videoContainerHeight = videoContainer?.offsetHeight || 0;
                const otherElementsHeight = 120; // ヘッダーやその他の要素の高さ
                chatHeight = windowHeight - (videoContainerHeight + otherElementsHeight);
            }
        };

        updateLayout();
        window.addEventListener('resize', updateLayout);
        window.addEventListener('orientationchange', updateLayout);

        setTimeout(updateLayout, 1000);

        return () => {
            window.removeEventListener('resize', updateLayout);
            window.removeEventListener('orientationchange', updateLayout);
        };
    }

    function initializeElapsedTime() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'live') return;

        startTime = new Date(Date.now() - 99 * 60 * 1000);
        const intervalId = setInterval(updateElapsedTime, 1000);

        return () => clearInterval(intervalId);
    }

    function initializeTimeUntilLive() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'upcoming') return;

        updateTimeUntilLive();
        const intervalId = setInterval(updateTimeUntilLive, 60000);

        return () => clearInterval(intervalId);
    }

    async function initializeLiveViewers() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'live') return;

        fetchLiveViewers();
        const liveInterval = setInterval(fetchLiveViewers, 20000);

        return () => clearInterval(liveInterval);
    }

    async function initializeLiveChatHistory() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'live') return;

        fetchLiveChatHistory();
        const chatInterval = setInterval(fetchLiveChatHistory, 60000);

        return () => clearInterval(chatInterval);
    }

    function initializeRankingState() {
        if (!browser) return;

        // const stored = localStorage.getItem('rankingVisible');
        // if (stored !== null) {
        //     isRankingVisible = stored === 'true';
        // }
        isRankingVisible = true;
    }

    function scrollToTop() {
        if (!browser) return;
        window.scrollTo(0, 0);
    }

    function splitIntoParagraphs(text: string): string[] {
        return text ? text.split('\n').filter(paragraph => paragraph.trim() !== '') : [];
    }

    function linkify(text: string): string {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text ? text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:underline">$1</a>') : '';
    }

    function toggleDescription() {
        isDescriptionExpanded = !isDescriptionExpanded;
    }

    function switchMode(mode: string) {
        currentMode = mode;
        if (isMobile && contentContainer) {
            contentContainer.scrollTop = 0;
        }
    }

    function toggleMoreOptions() {
        showMoreOptions = !showMoreOptions;
    }

    function goBack(event: MouseEvent | TouchEvent) {
        event.preventDefault();
        if (browser) {
            isRippling = true;
            if (window.history.length > 2) {
                window.history.back();
            } else {
                goto('/');
            }
        }
    }

    // function updateSafeArea() {
    //     if (!browser) return;
    //     const vh = window.innerHeight * 0.01;
    //     document.documentElement.style.setProperty('--vh', `${vh}px`);
    // }

    function updateElapsedTime() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'live') return;

        const now = new Date();
        const diff = now.getTime() - startTime.getTime();

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        // 変数に代入せずに返すだけに変更
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    function updateTimeUntilLive() {
        if (!videoDetails || videoDetails.live_broadcast_content !== 'upcoming') return;

        const now = new Date();
        const startTime = new Date(videoDetails.scheduled_start_time);
        const diff = startTime.getTime() - now.getTime();

        if (diff <= 0) {
            timeUntilLive = 'まもなく開始';
        } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            if (days > 0) {
                timeUntilLive = `${days}日後に配信開始`;
            } else if (hours > 0) {
                timeUntilLive = `${hours}時間${minutes}分後に配信開始`;
            } else {
                timeUntilLive = `${minutes}分後に配信開始`;
            }
        }
    }

    function formatPublishDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    // お気に入りボタンの処理関数を修正
    async function handleFavoriteClick(event: MouseEvent) {
        // イベント伝播を停止
        event.stopPropagation();
        
        if (!$authStore.user) {
            showLoginPopup = true;
            return;
        }

        const result = await favoriteStore.toggleFavorite(videoDetails);
        if (result !== null) {
            if (rippleTimeoutId) clearTimeout(rippleTimeoutId);
            rippleTimeoutId = setTimeout(() => {
                // リップルエフェクトの処理は不要なので空のままにします
            }, 1000) as unknown as number;
        }
    }

    function handleBackClick() {
        setTimeout(() => {
            history.back();
        }, 150);
    }

    async function fetchLiveChatHistory() {
        const { data: liveChatHistoryData, error: liveChatHistoryError } = await supabase
            .from('live_chat_history')
            .select('*')
            .eq('video_id', videoDetails.id);

        const videoHistory = liveChatHistoryData?.filter(data => data.video_id === videoDetails.id) ?? [];
        totalSuperChatJpy = videoHistory.reduce((acc, curr) => acc + curr.superchat_jpy, 0);
        totalCommentCount = videoHistory.reduce((acc, curr) => acc + curr.total_message_count, 0);
    }

    async function fetchLiveViewers() {
        const { data, error } = await supabase
            .from('video')
            .select('live_stats')
            .eq('id', videoDetails.id)
            .single();

        if (error) {
            console.error('Error fetching live viewers:', error);
            return;
        }

        const liveStats = data.live_stats;
        if (!liveStats) return;

        // データの差分をチェック
        const hasChanges = !previousLiveStats || 
            Object.keys(liveStats).length !== Object.keys(previousLiveStats).length ||
            Object.entries(liveStats).some(([key, value]) => 
                previousLiveStats[key] !== value
            );

        if (hasChanges) {
            // videoDetailsのlive_statsを更新
            videoDetails.live_stats = liveStats;
            previousLiveStats = liveStats;
            
            const latestTimestamp = Object.keys(liveStats).sort().pop();
            if (latestTimestamp) {
                concurrentViewers = liveStats[latestTimestamp];
            }

            // グラフを更新（showGraphの状態に関係なく）
            if (chartCanvas) {
                initializeChart();
            }
        }
    }

    // グラフの初期化と更新
    $: if (videoDetails?.live_stats && chartCanvas) {
        initializeChart();
    }

    function generateTitle(videoDetails: VideoDetails) {
        if (!videoDetails) return '';
        return `${videoDetails.title} - ${videoDetails.channel_title} | VTGrid`;
    }

    function generateDescription(videoDetails: VideoDetails) {
        if (!videoDetails) return '';

        if (videoDetails.live_broadcast_content === 'live') {
            return `${videoDetails.channel_title}が「${videoDetails.title}」を現在配信中！${formatViewCount(concurrentViewers)}人が視聴中。${videoDetails.description?.slice(0, 100) || ''}`;
        } else if (videoDetails.live_broadcast_content === 'upcoming') {
            return `${videoDetails.channel_title}の配信予定「${videoDetails.title}」${timeUntilLive ? `は${timeUntilLive}です。` : ''}${videoDetails.description?.slice(0, 100) || ''}`;
        } else {
            return `${videoDetails.channel_title}の配信アーカイブ「${videoDetails.title}」${formatViewCount(videoDetails.last_live_view_count)}回再生。${videoDetails.description?.slice(0, 100) || ''}`;
        }
    }

    function toggleRanking() {
        if (!browser) return;
        isRankingVisible = !isRankingVisible;
        localStorage.setItem('rankingVisible', isRankingVisible.toString());
    }

    function formatViewCount(count: number | null | undefined): string {
        if (count == null || isNaN(count)) return '♡';
        
        if (count >= 10000) {
            return `${(count / 10000).toFixed(1)}万`;
        }
        return count.toLocaleString();
    }

    function handleRelatedVideoClick(videoId: string) {
        goto(`/video/${videoId}`);
    }

    // イベントリスト専用の動画切り替え関数（ページ全体をリロード）
    function handleEventVideoClick(videoId: string) {
        if (browser) {
            // ページ全体をリロードして確実な初期化を行う
            window.location.href = `/video/${videoId}`;
        }
    }

    async function initializeLiveRankings() {
        await liveRankingStore.fetchRankings();
        
        const rankingInterval = setInterval(() => {
            liveRankingStore.fetchRankings();
        }, 20000);

        return () => {
            clearInterval(rankingInterval);
        };
    }

    function bindSelectedVideoButton(node: HTMLElement, isSelected: boolean) {
        if (isSelected) {
            selectedVideoButton = node;
        }
        return {
            destroy() {
                if (selectedVideoButton === node) {
                    // @ts-ignore
                    selectedVideoButton = undefined;
                }
            }
        };
    }

    function startDragging(e: MouseEvent) {
        isDragging = true;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
    }

    function stopDragging() {
        isDragging = false;
    }

    function onDrag(e: MouseEvent) {
        if (isDragging) {
            // 未使用の変数への代入を削除
            // 必要に応じて別の方法で位置を更新
        }
    }

    function formatDuration(duration: string): string {
        if (!duration) return '';
        
        // ISO 8601 duration形式をパース
        const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
        if (!match) return '';

        const hours = (match[1] || '').replace('H', '');
        const minutes = (match[2] || '').replace('M', '');
        const seconds = (match[3] || '').replace('S', '');

        // 時間、分、秒を数値に変換
        const h = parseInt(hours) || 0;
        const m = parseInt(minutes) || 0;
        const s = parseInt(seconds) || 0;

        // フォーマット
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        } else {
            return `${m}:${s.toString().padStart(2, '0')}`;
        }
    }

    onDestroy(() => {
        if (popupWindow) {
            try {
                // @ts-ignore - Window型の問題を無視
                popupWindow.close();
            } catch (e) {
                console.error('ポップアップウィンドウを閉じる際にエラーが発生しました:', e);
            }
        }
    });

    function initializeChart() {
        if (!videoDetails?.live_stats) return;
        
        const ctx = chartCanvas.getContext('2d');
        if (!ctx) return;
        
        if (viewerChart) {
            viewerChart.destroy();
        }
        
        // タイムスタンプでソート
        const timestamps = Object.keys(videoDetails.live_stats).sort((a, b) => parseInt(a) - parseInt(b));
        
        // タイムスタンプの形式を確認
        const isNumeric = timestamps.every(ts => !isNaN(Number(ts)));
        
        // 日本時間に変換して表示用のラベルを作成（時間のみ）
        const labels = timestamps.map((ts, index) => {
            let date;
            
            if (isNumeric) {
                // 数値の場合はミリ秒として解釈
                date = new Date(Number(ts));
            } else {
                // 文字列の場合はISO形式として解釈
                date = new Date(ts);
            }
            
            // 無効な日付の場合はインデックスを返す
            if (isNaN(date.getTime())) {
                return `${index + 1}`;
            }
            
            // 時間のみを表示（HH:MM形式）
            return date.toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false // 24時間表示
            });
        });
        
        const data = timestamps.map(timestamp => videoDetails.live_stats[timestamp]);
        
        viewerChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [{
                    label: '同時接続数',
                    data,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: (context) => {
                        // 最新のポイントを大きく表示
                        return context.dataIndex === data.length - 1 ? 6 : 2;
                    },
                    pointBackgroundColor: (context) => {
                        // 最新のポイントを赤色に
                        return context.dataIndex === data.length - 1 ? '#ef4444' : '#3b82f6';
                    },
                    pointBorderColor: (context) => {
                        // 最新のポイントの境界線を赤色に
                        return context.dataIndex === data.length - 1 ? '#ef4444' : '#3b82f6';
                    },
                    pointHoverRadius: 4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 500 // アニメーション時間を短く
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleFont: {
                            size: 12,
                            weight: 'bold'
                        },
                        bodyFont: {
                            size: 12
                        },
                        padding: 10,
                        cornerRadius: 4,
                        displayColors: false,
                        callbacks: {
                            label: function(context) {
                                return `視聴者数: ${context.parsed.y.toLocaleString()} 人`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(255, 255, 255, 0.1)'
                        },
                        ticks: {
                            color: '#9ca3af',
                            callback: function(value) {
                                return value.toLocaleString();
                            },
                            font: {
                                size: 11
                            }
                        }
                    },
                    x: {
                        grid: {
                            color: 'rgba(255, 255, 255, 0.1)'
                        },
                        ticks: {
                            color: '#9ca3af',
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 12,
                            font: {
                                size: 11
                            }
                        }
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            }
        });
    }

    // 視聴数の定期更新
    let updateInterval: NodeJS.Timeout;
    let previousLiveStats: Record<string, number> = {};
    let previousLabels: string[] | null = null;
    
    // live_statsの変更を監視
    $: if (videoDetails?.live_stats) {
        const currentLiveStats = videoDetails.live_stats;
        const currentLabels = Object.keys(currentLiveStats).sort((a, b) => parseInt(a) - parseInt(b));
        
        // データの差分をチェック
        const hasChanges = !previousLiveStats || 
            currentLabels.length !== previousLabels?.length ||
            currentLabels.some((label, index) => 
                previousLabels?.[index] !== label || 
                currentLiveStats[label] !== previousLiveStats[label]
            );
        
        if (hasChanges && showGraph && chartCanvas) {
            initializeChart();
            previousLiveStats = currentLiveStats;
            previousLabels = currentLabels;
        }
    }

    onMount(() => {
        // 20秒ごとに視聴数を更新
        updateInterval = setInterval(() => {
            if (videoDetails?.live_stats) {
                fetchLiveViewers();
            }
        }, 20000);

        return () => {
            if (updateInterval) {
                clearInterval(updateInterval);
            }
        };
    });

    // グラフの表示状態を監視
    $: if (showGraph && chartCanvas && videoDetails?.live_stats) {
        initializeChart();
    }

    // 同時接続数データを処理する関数
    function processViewerData(liveStats: Record<string, number> | undefined) {
        if (!liveStats) return { labels: [], data: [] };
        
        // タイムスタンプでソート
        const timestamps = Object.keys(liveStats).sort();
        
        // タイムスタンプの形式を確認
        const isNumeric = timestamps.every(ts => !isNaN(Number(ts)));
        
        // 日本時間に変換して表示用のラベルを作成（時間のみ）
        const labels = timestamps.map((ts, index) => {
            let date;
            
            if (isNumeric) {
                // 数値の場合はミリ秒として解釈
                date = new Date(Number(ts));
            } else {
                // 文字列の場合はISO形式として解釈
                date = new Date(ts);
            }
            
            // 無効な日付の場合はインデックスを返す
            if (isNaN(date.getTime())) {
                return `${index + 1}`;
            }
            
            // 時間のみを表示（HH:MM形式）
            return date.toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false // 24時間表示
            });
        });
        
        // 視聴者数データ
        const data = timestamps.map(ts => liveStats[ts]);
        
        return { labels, data };
    }

    // ウィンドウサイズの監視とレイアウト切り替え
    function handleResize() {
        const newLayout = window.innerWidth >= 1024 ? 'desktop' : 'mobile';
        
        // レイアウトが変わった時だけisMobileを更新
        if (newLayout !== currentLayout) {
            currentLayout = newLayout;
            isMobile = currentLayout === 'mobile';
        }
    }

    onMount(() => {
        if (browser) {
            handleResize(); // 初期化
            window.addEventListener('resize', handleResize);
        }
        
        // その他の初期化コード...
        return () => {
            if (browser) {
                window.removeEventListener('resize', handleResize);
            }
        };
    });

    // 配信リストのレイアウト設定
    let isGridLayout = true; // デフォルトでグリッドレイアウト（2列表示）

    function toggleListLayout() {
        isGridLayout = !isGridLayout;
        // ローカルストレージに設定を保存
        if (browser) {
            localStorage.setItem('streamListLayout', isGridLayout ? 'grid' : 'list');
        }
    }

    // 現在の動画のチャンネルが属するグループを検索する関数
    function findChannelGroup(channelId: string) {
        if (!data.groupChannels || !groups) return null;
        
        const channelGroup = data.groupChannels.find(channel => 
            channel.id === channelId
        );
        
        if (channelGroup) {
            return channelGroup.group_id;
        }
        
        return null;
    }

    onMount(() => {
        // ローカルストレージから表示設定を読み込む
        if (browser) {
            const savedLayout = localStorage.getItem('streamListLayout');
            if (savedLayout) {
                isGridLayout = savedLayout === 'grid';
            }

            // 動画のチャンネルが属するグループを自動選択
            if (videoDetails && videoDetails.channel_id) {
                const groupId = findChannelGroup(videoDetails.channel_id);
                if (groupId) {
                    selectedGroup = groupId;
                }
            }
        }
    });

    // 初期化関数（DOMの準備ができたら実行）
    onMount(() => {
        // DOM要素への参照を確立
        contentContainer = document.querySelector('.content-container') as HTMLElement;
    });

    // 継続トークンのみを更新する関数（iframeの再読み込みなし）
    function updateContinuationToken(seekMs: number) {
        if (!videoDetails || !videoDetails.id || !videoDetails.channel_id || !iframeElement) return;
        
        console.log('継続トークンのみを更新します:', seekMs, 'ミリ秒');
        
        // APIから継続トークンのみを取得
        fetch(`/api/youtube-chat?videoId=${videoDetails.id}&seekMs=${seekMs}&embedDomain=${embedDomain}`)
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                    console.error('APIエラー:', data.error);
                    return;
                }
                
                // 継続トークンを含むURLがあるか確認
                if (!data.basicChatUrl && !data.chatUrl) {
                    console.error('継続トークンを含むURLが返されませんでした');
                    return;
                }
                
                // iframeElementが存在するか再確認
                if (!iframeElement) {
                    console.error('iframeElementが存在しません');
                    return;
                }
                
                // 現在のURLを記録
                const currentUrl = iframeElement.src;
                console.log('現在のURL:', currentUrl);
                
                // 新しいURLを取得（基本URLを優先）
                const newUrl = data.basicChatUrl || data.chatUrl;
                console.log('新しいURL:', newUrl);
                
                // postMessageで現在のiframeに継続トークンを適用するよう要求
                if (iframeElement.contentWindow) {
                    iframeElement.contentWindow.postMessage({
                        action: 'updateContinuation',
                        url: newUrl,
                        seekMs: seekMs
                    }, '*');
                    console.log('継続トークン更新メッセージを送信しました');
                    
                    // URLが大幅に異なる場合のみiframeを更新
                    const urlsAreDifferent = currentUrl.split('?')[0] !== newUrl.split('?')[0];
                    if (urlsAreDifferent) {
                        console.log('URLのベース部分が異なるため、iframeを再読み込みします');
                        iframeElement.src = newUrl;
                    }
                }
            })
            .catch(error => {
                console.error('継続トークン更新中にエラー:', error);
            });
    }

    // アーカイブチャットを初期化する関数
    function initializeArchiveChat(seekMs = 0) {
        if (!videoDetails || !videoDetails.id || !videoDetails.channel_id) return;
        
        try {
            console.log('アーカイブチャットを初期化します:', seekMs, 'ミリ秒から');
            
            // APIからチャットURLとスクリプトを取得
            fetch(`/api/youtube-chat?videoId=${videoDetails.id}&seekMs=${seekMs}&embedDomain=${embedDomain}`)
                .then(response => response.json())
                .then(data => {
                    if (data.error) {
                        console.error('APIエラー:', data.error);
                        return;
                    }
                    
                    // 優先的に基本URLを使用（embed_domainなしのシンプルなURL）
                    const chatUrl = data.basicChatUrl || data.chatUrl;
                    console.log('構築したアーカイブチャットURL:', chatUrl);
                    
                    // URLが変わった場合のみiframeを更新
                    const shouldReloadIframe = !archiveChatUrl || archiveChatUrl !== chatUrl;
                    
                    console.log('iframeの更新が必要か:', shouldReloadIframe);
                    console.log('前のURL:', archiveChatUrl);
                    console.log('新しいURL:', chatUrl);
                    
                    // URLを保存
                    archiveChatUrl = chatUrl;
                    
                    // iframeがすでに存在する場合
                    if (iframeElement) {
                        if (shouldReloadIframe) {
                            console.log('URLが変更されたため、iframeを再読み込みします');
                            // iframeを更新
                            iframeElement.src = chatUrl;
                            
                            // iframe更新後、再生位置を同期
                            setTimeout(() => {
                                if (iframeElement && iframeElement.contentWindow) {
                                    // 現在の再生位置を送信
                                    iframeElement.contentWindow.postMessage({
                                        "yt-player-video-progress": seekMs / 1000
                                    }, "*");
                                }
                            }, 2000);
                        } else {
                            console.log('URLに変更がないため、再読み込みせずに位置のみ同期します');
                            syncChatPosition(iframeElement, seekMs / 1000);
                        }
                    } else {
                        console.log('iframeがまだないので新規作成します');
                    }
                })
                .catch(error => {
                    console.error('アーカイブチャット初期化中にAPIエラー:', error);
                });
        } catch (e) {
            console.error('アーカイブチャット初期化中にエラー:', e);
        }
    }

</script>

<svelte:head>
    <title>{pageTitle}</title>
    <meta name="description" content="VTuber {videoDetails.channel_title}の配信・アーカイブ「{videoDetails.title}」をVTGridで視聴 {videoDetails.description ? videoDetails.description.slice(0, 150) : ''}..." />
    <meta property="og:description" content={pageDescription} />
    <meta name="keywords" content="VTuber, {videoDetails.platform}, バーチャルYouTuber, 配信, アーカイブ, {videoDetails.channel_title}, {videoDetails.title} " />
    <meta property="og:image" content={videoDetails.thumbnail_url} />
    <meta property="og:url" content={`https://vt-grid.com/video/${$page.params.id}`} />
    <meta name="twitter:card" content="summary" />
    <meta name="twitter:title" content={videoDetails.title} />
    <meta name="twitter:description" content={videoDetails.description} />
    <meta name="twitter:image" content={videoDetails.thumbnail_url} />
    <meta name="robots" content="index, follow">
    <link rel="canonical" href="https://vt-grid.com/video/${$page.params.id}" />
    
    <!-- YouTube IFrame API用の型定義 -->
    {@html `
    <script>
        // YouTube IFrame API用のグローバル変数
        var YT;
        var onYouTubeIframeAPIReady;
    </script>
    `}

    {@html `
        <script type="application/ld+json">
        {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "${videoDetails.title.replace(/"/g, '\\"')}",
            "description": "${videoDetails.description ? videoDetails.description.replace(/"/g, '\\"') : ''}",
            "thumbnailUrl": "${videoDetails.thumbnail_url}",
            "uploadDate": "${videoDetails.published_at}",
            ${videoDetails.duration ? `"duration": "${videoDetails.duration}",` : ''}
            "embedUrl": "https://www.youtube.com/embed/${videoDetails.id}",            
            "author": {
                "@type": "Person",
                "name": "${videoDetails.channel_title.replace(/"/g, '\\"')}",
                "url": "${videoDetails.channel_id}",
                "image": "${channelData?.thumbnail || ''}"
            },
            "publication": {
                "@type": "BroadcastEvent",
                "isLiveBroadcast": ${videoDetails.live_broadcast_content === 'live'},
                "startDate": "${videoDetails.scheduled_start_time || videoDetails.published_at}",
                "inLanguage": "ja"
            },
            "isFamilyFriendly": true,
            "genre": "VTuber",
            "keywords": "VTuber,${videoDetails.channel_title},配信,${videoDetails.live_broadcast_content === 'live' ? 'ライブ配信,配信中' : 'アーカイブ'}",
            "inLanguage": "ja",
            "url": "https://vt-grid.com/video/${videoDetails.id}",
            "potentialAction": {
                "@type": "WatchAction",
                "target": "https://vt-grid.com/video/${videoDetails.id}"
            }
        }
        </script>
        `}
</svelte:head>

{#if videoDetails}
    <div class="text-gray-900 dark:text-white" class:overflow-hidden={isMobile}>
        <!-- PC表示 -->
        <div class="hidden lg:flex h-screen w-full mx-auto relative overflow-hidden">
            <!-- サイドバー (配信リスト) -->
            {#if showSidebar}
                <aside class="w-[330px] pt-3 flex flex-col transition-all duration-300 ease-in-out" transition:slide={{ duration: 300, axis: 'x' }}>
                    <div class="p-2 flex flex-col gap-2">
                        <!-- イベント情報セクション -->
                        {#if eventInfo && eventInfo.hasEvent}
                            <Collapsible 
                                open={!isEventCollapsed} 
                                class="w-full rounded-lg shadow-md bg-background/80 dark:bg-zinc-900/90 border border-border overflow-hidden flex flex-col" style="max-height: 85vh;"
                                on:toggle={(e) => isEventCollapsed = !e.detail}
                            >
                                <div slot="header" class="w-full">
                                    <div class="py-3 px-4 flex justify-between items-center">
                                        <h3 class="text-base font-semibold text-foreground flex items-center gap-2">
                                            <Calendar class="h-5 w-5 text-primary" />
                                            {eventInfo.event.name}
                                        </h3>
                                    </div>
                                </div>

                                <div class="p-4 space-y-4 flex-1 overflow-hidden flex flex-col">
                                    <!-- セクション情報 -->
                                    <div class="bg-muted/60 rounded-lg p-4">
                                        <h4 class="text-base font-semibold text-foreground mb-2">
                                            {eventInfo.currentSection.name}
                                        </h4>
                                        {#if eventInfo.currentSection.description}
                                            <p class="text-sm text-muted-foreground leading-relaxed">
                                                {eventInfo.currentSection.description}
                                            </p>
                                        {/if}
                                    </div>

                                    <!-- セクション内の動画リスト -->
                                    <div class="space-y-3">
                                        <div class="text-sm text-muted-foreground flex items-center justify-between font-medium">
                                            <span>このセクションの動画</span>
                                            <span class="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs font-semibold">{eventInfo.currentSectionVideos.length}本</span>
                                        </div>
                                        
                                        <div class="overflow-y-auto space-y-2 pr-2 event-scroll" style="max-height: calc(85vh - 350px);">
                                            {#each eventInfo.currentSectionVideos as video, index (video.id)}
                                                <button
                                                    class="w-full flex gap-3 p-3 rounded-lg text-left event-video-button {video.is_current 
                                                        ? 'bg-primary/15 border-2 border-primary/40 shadow-sm' 
                                                        : 'hover:bg-muted/60 border border-transparent hover:border-border'}"
                                                    on:click={() => handleEventVideoClick(video.id)}
                                                >
                                                    <div class="relative w-20 h-11 bg-black rounded-md overflow-hidden flex-shrink-0 shadow-sm">
                                                        <img 
                                                            src={video.thumbnail_url} 
                                                            alt={video.title}
                                                            class="w-full h-full object-cover"
                                                        />
                                                        
                                                        <!-- 現在再生中インジケーター -->
                                                        {#if video.is_current}
                                                            <div class="absolute inset-0 bg-primary/30 flex items-center justify-center">
                                                                <PlayCircle class="h-5 w-5 text-primary drop-shadow-lg" />
                                                            </div>
                                                        {/if}
                                                        
                                                        <!-- 動画タイプインジケーター -->
                                                        {#if getVideoType(video) === 'live'}
                                                            <div class="absolute top-1 left-1 px-1.5 py-0.5 bg-red-500 rounded text-xs text-white font-bold shadow-md">
                                                                LIVE
                                                            </div>
                                                        {:else if getVideoType(video) === 'upcoming'}
                                                            <div class="absolute top-1 left-1 px-1.5 py-0.5 bg-blue-500 rounded text-xs text-white font-bold shadow-md">
                                                                予定
                                                            </div>
                                                        {/if}
                                                    </div>
                                                    
                                                    <div class="flex-1 min-w-0 space-y-1.5">
                                                        <p class="text-sm font-medium line-clamp-2 text-foreground leading-relaxed {video.is_current ? 'text-primary font-semibold' : ''}">
                                                            {video.title}
                                                        </p>
                                                        <div class="space-y-1">
                                                            <div class="text-xs text-muted-foreground">
                                                                <span class="font-medium leading-tight truncate block">{video.channel_title}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                            {/each}
                                        </div>
                                    </div>
                                    
                                    <!-- イベントページへのリンク -->
                                    <div class="pt-3 border-t border-border">
                                        <a 
                                            href="/events/{eventInfo.event.id}"
                                            class="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium py-2 px-3 rounded-md hover:bg-primary/5 w-full justify-center"
                                        >
                                            <Calendar class="h-4 w-4" />
                                            イベントページを見る
                                        </a>
                                    </div>
                                </div>
                            </Collapsible>
                        {:else}
                            <!-- 配信中セクション（イベント情報がない場合のみ表示） -->
                        <Collapsible 
                            open={!isLiveListCollapsed} 
                            class="w-full rounded-lg shadow-md bg-background/70 dark:bg-zinc-900/80 border border-border overflow-hidden flex flex-col {!isLiveListCollapsed ? 'h-[85vh]' : 'h-auto'}"
                            on:toggle={(e) => isLiveListCollapsed = !e.detail}
                        >
                            <div slot="header" class="w-full flex-shrink-0">
                                <div class="py-2 px-4 flex justify-between items-center">
                                    <h3 class="text-sm font-medium text-foreground flex items-center gap-2">
                                        <span class="relative flex h-2 w-2">
                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                        </span>
                                        {t('video_page.live_now', '配信中')} 
                                        {#if isLiveListCollapsed}
                                            <span class="text-xs text-muted-foreground ml-2">({$liveRankingStore.length})</span>
                                        {/if}
                                    </h3>
                                </div>
                            </div>

                            <!-- コンテンツ部分 (折りたたまれる) -->
                            <div class="p-4 flex-grow flex flex-col overflow-hidden">
                                <div class="flex items-center gap-2 mb-3 flex-shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            on:click={(e) => {
                                                e.stopPropagation();
                                                toggleListLayout();
                                            }}
                                            class="h-8 px-2 text-xs"
                                        >
                                            {#if isGridLayout}
                                                <svg class="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                                </svg>
                                                <span>リスト</span>
                                            {:else}
                                                <svg class="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                    <rect x="3" y="3" width="7" height="7" rx="1" />
                                                    <rect x="14" y="3" width="7" height="7" rx="1" />
                                                    <rect x="3" y="14" width="7" height="7" rx="1" />
                                                    <rect x="14" y="14" width="7" height="7" rx="1" />
                                                </svg>
                                                <span>グリッド</span>
                                            {/if}
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            class="h-8 px-2"
                                            on:click={(e) => e.stopPropagation()}
                                        >
                                            <a href="/multiview" class="flex items-center gap-1 text-xs">
                                                <Grid size={14} class="text-primary" />
                                                <span>マルチビュー</span>
                                            </a>
                                        </Button>
                                    </div>
                                <div class="relative mb-3 flex-shrink-0">
                                    <Search class="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="search"
                                        bind:value={searchKeyword}
                                        placeholder="配信を検索..."
                                        class="w-full pl-9"
                                    />
                                </div>

                                <div class="relative mb-3 flex-shrink-0">
                                    <select
                                        bind:value={selectedGroup}
                                        class="w-full h-9 bg-background border border-input rounded-md py-2 pl-3 pr-8 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 appearance-none"
                                    >
                                        <option value="" class="bg-background">全て ({$liveRankingStore.length})</option>
                                        <option value="followed" class="bg-background">フォロー中 ({followedLiveStreams.length})</option>
                                        {#each groups || [] as group}
                                            {@const count = $liveRankingStore.filter(video => 
                                                data.groupChannels?.some(channel => 
                                                    channel.id === video.channel_id && channel.group_id === group.id
                                                ) || false
                                            ).length}
                                            <option value={group.id} class="bg-background">{group.name} ({count})</option>
                                        {/each}
                                    </select>
                                    <ChevronDown class="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                </div>

                                <div class="overflow-y-auto flex-grow" style="max-height: calc(85vh - 220px);">
                                    <div class="pr-3">
                                        {#if filteredLiveRankings.length === 0}
                                                <div class="text-sm text-muted-foreground px-2">
                                                配信中の動画はありません
                                            </div>
                                        {:else}
                                            <div class={isGridLayout ? "grid grid-cols-2 gap-2" : "flex flex-col gap-1"}>
                                                {#each filteredLiveRankings as video, i (video.id)}
                                                    <button 
                                                        animate:flip={{duration: 500, easing: quintOut}}
                                                        on:click={() => handleRelatedVideoClick(video.id)}
                                                        on:keydown={(e) => e.key === 'Enter' && handleRelatedVideoClick(video.id)}
                                                        class={isGridLayout 
                                                            ? "flex-shrink-0 w-full group text-left relative transition-all duration-200 p-0.5" + (video.id === videoDetails.id ? " scale-[1.02] shadow-lg shadow-primary/20" : "")
                                                            : "w-full flex gap-3 p-2 rounded-lg mb-1 hover:bg-accent/50 transition-colors" + (video.id === videoDetails.id ? " bg-accent/50 dark:bg-accent/30 ring-1 ring-primary" : "")
                                                        }
                                                        data-video-id={video.id}
                                                        use:bindSelectedVideoButton={video.id === videoDetails.id}
                                                    >
                                                        {#if isGridLayout}
                                                            {#if video.id === videoDetails.id}
                                                                <div class="absolute inset-0.5 ring-2 ring-primary rounded-lg z-10"></div>
                                                                <div class="absolute top-0 right-0 px-1.5 py-0.5 bg-gradient-to-r from-primary/80 to-primary/60 text-primary-foreground text-[10px] rounded-sm font-medium flex items-center gap-1 z-20">
                                                                    <span class="w-1 h-1 rounded-full bg-primary-foreground animate-pulse"></span>
                                                                    再生中
                                                                </div>
                                                            {/if}
                                                            <div class="relative w-full pb-[56.25%] bg-black rounded-lg overflow-hidden">
                                                                <img 
                                                                    src={video.thumbnail_url} 
                                                                    alt={video.title}
                                                                    class="absolute top-0 left-0 w-full h-full object-cover"
                                                                />
                                                                <div class="absolute top-0 left-0 px-1 py-0.5 bg-red-500 rounded text-[10px] text-white font-medium flex items-center gap-0.5">
                                                                    <span class="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                                                                    LIVE
                                                                </div>
                                                                <div class="absolute bottom-0 right-0 px-1 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
                                                                    <div class="flex items-center gap-1">
                                                                        <Eye class="h-3 w-3 text-red-500" />
                                                                        {formatViewCount(video.currentViewers)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div class="mt-1.5 text-xs">
                                                                <p class="font-medium line-clamp-1 text-foreground text-[10px]">
                                                                    {video.title}
                                                                </p>
                                                                <p class="text-muted-foreground line-clamp-1 mt-0.5 text-[9px]">
                                                                    {video.channel_title}
                                                                </p>
                                                            </div>
                                                        {:else}
                                                            <div class="relative w-32 flex-shrink-0">
                                                                <div class="w-full pb-[56.25%] relative rounded overflow-hidden">
                                                                    <img 
                                                                        src={video.thumbnail_url} 
                                                                        alt={video.title}
                                                                        class="absolute inset-0 w-full h-full object-cover"
                                                                    />
                                                                    <div class="absolute top-1 left-1 px-1 py-0.5 bg-red-500 rounded text-[10px] text-white font-medium flex items-center gap-0.5">
                                                                        <span class="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                                                                        LIVE
                                                                    </div>
                                                                    <div class="absolute bottom-1 right-1 px-1 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
                                                                        <div class="flex items-center gap-1">
                                                                            <Eye class="h-3 w-3 text-red-500" />
                                                                            {formatViewCount(video.currentViewers)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div class="flex-1 min-w-0 text-left">
                                                                <p class="text-xs font-medium text-foreground line-clamp-2 mb-1">
                                                                    {video.title}
                                                                </p>
                                                                <p class="text-[11px] text-muted-foreground">
                                                                    {video.channel_title}
                                                                </p>
                                                            </div>
                                                        {/if}
                                                    </button>
                                                {/each}
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            </div>
                        </Collapsible>
                                                                {/if}
                    </div>
                </aside>
            {/if}

            <!-- メインコンテンツ -->
            <div class="flex-1 flex flex-col overflow-y-auto pt-3">
                
                <!-- 動画プレーヤーとチャット -->
                <div class="flex-1 p-3">
                    <div class="flex gap-2 h-full max-w-[1800px] mx-auto">
                        <!-- 左側: 動画プレーヤーと情報 -->
                        <div class="flex-1 flex flex-col min-w-0">
                            <!-- 動画プレーヤーとグラフをラップ -->
                            <div class="flex flex-col h-auto">
                                <!-- 動画プレーヤー -->
                                <div class="relative w-full overflow-hidden shadow-sm aspect-video">
                                    <VideoPlayer 
                                        bind:this={videoPlayerComponent}
                                        {videoDetails}
                                        {channelData}
                                        {embedDomain}
                                        bind:videoContainer={videoContainer}
                                        on:chatSync={handleChatSync}
                                        on:significantTimeChange={handleSignificantTimeChange}
                                    />
                                    
                                    <!-- グラフ -->
                                    {#if showGraph}
                                        <div class="absolute bottom-12 left-4 w-60 h-36 bg-gray-800/90 dark:bg-black/80 backdrop-blur-sm rounded-lg p-2 z-10" transition:fade>
                                            <button
                                                class="absolute top-1 right-1 p-1 rounded-full text-gray-300 dark:text-white/70 hover:text-white transition-colors z-10"
                                                on:click={() => showGraph = !showGraph}
                                            >
                                                <X size={16} />
                                            </button>
                                            <div class="w-full h-full">
                                                <canvas bind:this={chartCanvas} class="w-full h-full"></canvas>
                                            </div>
                                        </div>
                                    {/if}
                                </div>
                                
                                <!-- 動画プレーヤー下部コントロール -->
                                <div class="p-2 bg-black/5 dark:bg-zinc-900 rounded-b-lg border flex justify-between items-center">
                                    <div class="flex items-center gap-1">
                                        <Button 
                                            variant="secondary" 
                                            size="sm" 
                                            on:click={() => showSidebar = !showSidebar}
                                            class="h-8 px-3 flex items-center gap-1.5 rounded"
                                        >
                                            <Search size={16} class="text-foreground/70" />
                                            <span class="text-xs font-medium">{showSidebar ? 'サイドバーを閉じる' : 'サイドバーを開く'}</span>
                                        </Button>
                                        
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            on:click={() => {
                                                if (showChat) {
                                                    showChat = false;
                                                } else {
                                                    showChat = true;
                                                    isLiveChatCollapsed = false;
                                                }
                                            }}
                                            class="h-8 px-3 flex items-center gap-1.5 rounded"
                                        >
                                            <MessageCircle size={16} class="text-foreground/70" />
                                            <span class="text-xs font-medium">
                                                {!showChat ? 'チャットを表示' : 'チャットを非表示'}
                                            </span>
                                        </Button>
                                    </div>
                                    
                                    <div class="flex items-center gap-2">                                        
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            on:click={() => window.open(`https://www.youtube.com/watch?v=${videoDetails.id}`, '_blank')}
                                            title="YouTubeで開く"
                                            class="h-9 w-9 flex items-center justify-center rounded"
                                        >
                                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" fill="#FF0000"/>
                                            <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FFFFFF"/>
                                        </svg>                                        </Button>
                                        
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            on:click={() => showGraph = !showGraph}
                                            title="視聴数グラフ"
                                            class="h-9 w-9 flex items-center justify-center rounded"
                                        >
                                            <BarChart2 class="w-5 h-5 text-foreground/80" />
                                        </Button>
                                    </div>
                                </div>
                                
                                <!-- 動画情報 -->
                                <div class="flex flex-col gap-3 mt-3">
                                    <Card class="bg-card/90 dark:bg-zinc-900/90 backdrop-blur-sm">
                                        <CardHeader class="pb-2">
                                            <CardTitle class="text-xl font-semibold">
                                                {videoDetails.title}
                                            </CardTitle>
                                            
                                            <div class="flex items-center justify-between mt-2">
                                                <div class="flex items-center gap-2 text-[15px]">
                                                    <Badge 
                                                        variant="outline" 
                                                        class={`flex items-center gap-1.5 py-1 px-3 ${
                                                            videoDetails.live_broadcast_content === 'live' 
                                                                ? 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-200 border-red-200 dark:border-red-800/50' 
                                                                : 'bg-gray-100 dark:bg-zinc-800/80'
                                                        }`}
                                                    >
                                                        {#if videoDetails.live_broadcast_content === 'live'}
                                                            <span class="w-2 h-2 rounded-full bg-red-500"></span>
                                                            <span class="font-medium">{formatViewCount(concurrentViewers)}</span>
                                                            <span>視聴中</span>
                                                        {:else if videoDetails.live_broadcast_content === 'none'}
                                                            <span class="font-medium">{formatViewCount(maxConcurrentViewers)}</span>
                                                            <span class="text-xs">最高同時視聴数</span>
                                                        {/if}
                                                    </Badge>
                                                    <time class="text-muted-foreground ml-2" datetime={videoDetails.published_at}>
                                                        {formatDate(videoDetails.published_at)}
                                                    </time>
                                                </div>

                                                <!-- アクション -->
                                                <div class="flex items-center gap-2">
                                                    <!-- お気に入りボタン -->
                                                    <Button
                                                        variant={isFavorite ? "default" : "ghost"}
                                                        size="sm"
                                                        on:click={handleFavoriteClick}
                                                        class="flex items-center gap-1.5"
                                                    >
                                                        <Heart
                                                            class="w-4 h-4"
                                                            fill={isFavorite ? '#ef4444' : 'none'}
                                                            color={isFavorite ? '#ef4444' : 'currentColor'}
                                                        />
                                                        <span class="text-xs font-medium">{isFavorite ? 'お気に入り解除' : 'お気に入り'}</span>
                                                    </Button>

                                                    <ShareButton 
                                                        url={`/video/${videoDetails.id}`}
                                                        title={videoDetails.title}
                                                    />
                                                </div>
                                            </div>
                                        </CardHeader>

                                        <Separator />

                                        <CardContent class="pt-4">
                                            <!-- チャンネル情報 -->
                                            <div class="flex flex-col">
                                                <div class="w-fit">
                                                    <a
                                                        href="/channel/{videoDetails.channel_id}"
                                                        class="flex items-center gap-3 hover:bg-accent/50 dark:hover:bg-accent/30 rounded-lg p-2 -ml-2 transition-colors"
                                                    >
                                                        <div class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border">
                                                            <img
                                                                src={channelData?.thumbnail}
                                                                alt={videoDetails.channel_title}
                                                                class="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                        <div class="flex flex-col">
                                                            <span class="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                                                {videoDetails.channel_title}
                                                            </span>
                                                            <span class="text-xs text-muted-foreground">
                                                                {channelData?.subscriber_count ? `${formatNumber(channelData.subscriber_count)}人のチャンネル登録者` : ''}
                                                            </span>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>

                                            <!-- 説明文 -->
                                            <div class="text-[15px] text-foreground/80 space-y-4 mt-4">
                                                {#each visibleParagraphs as paragraph}
                                                    <p class="whitespace-pre-wrap leading-relaxed">
                                                        {@html linkify(paragraph)}
                                                    </p>
                                                {/each}
                                                
                                                {#if paragraphs.length > MAX_VISIBLE_PARAGRAPHS}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        on:click={toggleDescription}
                                                        class="text-[14px] text-muted-foreground hover:text-foreground transition-colors font-medium p-0 h-auto"
                                                    >
                                                        {isDescriptionExpanded ? '一部を表示' : 'もっと見る'}
                                                    </Button>
                                                {/if}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 右側: チャット -->
                        {#if showChat}
                        <div class="w-[370px] flex-shrink-0 transition-all duration-300 ease-in-out">
                            <Collapsible 
                                open={!isLiveChatCollapsed} 
                                class="w-full h-fit rounded-lg shadow-md bg-background/70 dark:bg-zinc-900/80 border border-border overflow-hidden"
                                on:toggle={(e) => isLiveChatCollapsed = !e.detail}
                            >
                                <div slot="header" class="w-full">
                                    <div class="py-2 px-4 flex justify-between items-center">
                                        <h3 class="text-sm font-medium text-foreground flex items-center gap-2">
                                            {videoDetails.live_broadcast_content === 'live' ? 'ライブチャット' : 'アーカイブチャット'}
                                            {#if isLiveChatCollapsed}
                                                <span class="text-xs text-muted-foreground ml-2">
                                                    {videoDetails.live_broadcast_content === 'live' 
                                                        ? `(${formatViewCount(concurrentViewers)}人が視聴中)` 
                                                        : ''}
                                                </span>
                                            {/if}
                                        </h3>
        
                                    </div>
                                </div>

                                <!-- チャット表示エリア (折りたたまれる) -->
                                <div style="height: calc(61vw * 0.67);" class="overflow-hidden">
                                    {#if currentMode === MODES.CHAT && videoDetails.live_broadcast_content === 'live'}
                                        {#if videoDetails.platform === 'youtube'}
                                            <iframe
                                                src={chatUrl}
                                                title="YouTube Chat"
                                                frameborder="0"
                                                width="100%"
                                                height="100%"
                                            ></iframe>
                                        {:else if videoDetails.platform === 'twitch' && channelData?.twitch_id}
                                            <iframe
                                                src="https://www.twitch.tv/embed/{channelData.twitch_id}/chat?darkpopout&parent={embedDomain}"
                                                frameborder="0"
                                                width="100%"
                                                height="100%"
                                            ></iframe>
                                        {/if}
                                    {:else if extensionDetected && videoDetails.platform === 'youtube' && videoDetails.live_broadcast_content !== 'live'}
                                        <div class="relative w-full h-full">
                                            <iframe
                                                src={archiveChatUrl}
                                                title="YouTube Archive Chat"
                                                frameborder="0"
                                                width="100%"
                                                height="100%"
                                                bind:this={iframeElement}
                                                on:load={() => {
                                                    console.log('アーカイブチャットiframeがロードされました');
                                                    
                                                    // chrome-extension:// のURLをチェックしてエラーの場合は再読み込み
                                                    if (iframeElement && iframeElement.src && iframeElement.src.startsWith('chrome-extension://')) {
                                                        console.log('chrome-extension URLを検出、リロードします');
                                                        initializeArchiveChat();
                                                    }
                                                    
                                                    // 現在の再生位置で同期（必要最小限の機能のみ残す）
                                                    if (iframeElement && iframeElement.contentWindow) {
                                                        // ロード直後は明示的に0秒から始める
                                                        // const initialTime = 0;
                                                        // syncChatPosition(iframeElement, initialTime);
                                                        
                                                        // 少し時間を置いてから現在の実際の再生位置を送信
                                                        setTimeout(() => {
                                                            if (iframeElement && iframeElement.contentWindow) {
                                                                syncChatPosition(iframeElement, videoTime);
                                                            }
                                                        }, 2000);
                                                    }
                                                }}
                                            ></iframe>
                                        </div>
                                    {:else if extensionDetected === false && browser && videoDetails.platform === 'youtube' && videoDetails.live_broadcast_content !== 'live'}
                                        <div class="p-4 flex flex-col items-center justify-center h-full">
                                            <div class="bg-accent/50 dark:bg-zinc-800/80 rounded-lg p-6 max-w-[300px] text-center">
                                                <p class="text-sm text-muted-foreground mb-4">アーカイブのコメント表示機能を近日中にリリース予定です。</p>
                                            </div>
                                        </div>
                                    {:else}
                                        <div class="p-4 flex flex-col items-center justify-center h-full">
                                            <p class="text-muted-foreground">このビデオにはライブチャットがありません。</p>
                                        </div>
                                    {/if}
                                </div>
                            </Collapsible>
                        </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>

        <!-- スマホ表示 -->
        <div class="lg:hidden flex flex-col mobile-content">
            <div class="w-full">
                <div class="aspect-w-16 aspect-h-9">
                </div>
            </div>

            <MobileHeader live_broadcast_content={videoDetails.live_broadcast_content} />

            <div class="flex-grow bg-white dark:bg-zinc-900 overflow-hidden flex flex-col">
                <MobileNavigation 
                    {videoDetails}
                    {channelData}
                    {totalSuperChatJpy}
                    {totalCommentCount}
                    {concurrentViewers}
                    {maxConcurrentViewers}
                    {currentMode}
                    handleBackClick={handleBackClick}
                    switchMode={switchMode}
                />
                
                <MobileContent
                    bind:contentContainer={contentContainer}
                    {currentMode}
                    {videoDetails}
                    {channelData}
                    {chatUrl}
                    {browser}
                    {visibleParagraphs}
                    {paragraphs}
                    {MAX_VISIBLE_PARAGRAPHS}
                    bind:isDescriptionExpanded={isDescriptionExpanded}
                    {isFavorite}
                    {relatedVideos}
                />
            </div>
        </div>
    </div>
{:else}
    <div class="flex justify-center items-center h-screen">
        <p class="text-2xl text-gray-600 dark:text-gray-400">動画が見つかりませんでした。</p>
    </div>
{/if}

{#if showLoginPopup}
    <LoginPopup 
        message={t('video_page.login_to_favorite', 'お気に入りに追加するにはログインしてください')}
        on:close={() => showLoginPopup = false} 
    />
{/if}

{#if $liveRankingStore.length > 0}
<MobileRanking 
    bind:isRankingVisible={isRankingVisible}
    liveRankingStore={liveRankingStore}
    bind:selectedGroup={selectedGroup}
    {followedLiveStreams}
    {groups}
    {filteredLiveRankings}
    {videoDetails}
    {data}
    {bindSelectedVideoButton}
/>
{/if}

<svelte:window
    on:mousemove={onDrag}
    on:mouseup={stopDragging}
/>

<style>
    /* アスペクト比を維持するためのスタイル */
    .aspect-w-16 {
        position: relative;
        padding-bottom: 56.25%; /* 16:9 のアスペクト比 */
    }
    .aspect-w-16 iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    /* スムーズなスクロール */
    html {
        scroll-behavior: smooth;
    }

    /* カスタムスクロールバー */
    ::-webkit-scrollbar {
        width: 10px;
    }

    ::-webkit-scrollbar-track {
        background: #f3f4f6;
        @apply dark:bg-neutral-800;
    }

    ::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 5px;
        @apply dark:bg-neutral-600;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
        @apply dark:bg-neutral-500;
    }

    :global(body) {
        margin: 0;
        padding: 0;
        overflow-x: hidden; /* 横スクロールを無効化 */
    }

    /* PC表示時のスクロールを有効にする */
    @media (min-width: 1024px) {
        :global(body) {
            overflow-y: auto !important;
            overflow-x: hidden !important; /* 横スクロールは常に無効 */
        }
    }

    .mobile-content {
        height: calc(100 * var(--vh, 1vh));
        overflow: hidden; /* はみ出しを防ぐ */
    }
    .content-area {
        height: calc(100 * var(--vh, 1vh) - 56px); /* 56pxはヘッダーの高さ */
    }

    .back-button {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
    }

    .ripple-container {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 350%;
        height: 350%;
        transform: translate(-50%, -50%);
        pointer-events: none;
    }

    .ripple {
        animation: ripple 0.6s ease-out;
        fill: currentColor;
        opacity: 0.5;
    }

    .pb-safe {
        padding-bottom: env(safe-area-inset-bottom);
    }

    .removing {
        animation: remove 0.5s ease-out forwards;
    }

    @keyframes remove {
        0% {
            opacity: 1;
            transform: scale(1);
        }
        100% {
            opacity: 0;
            transform: scale(0.8);
        }
    }

    /* チャットiframeのスタイル追加 */
    iframe {
        min-width: 380px;  /* 最低幅を380pxに増加 */
        font-size: 14px !important;
    }

    /* コンテナのスタイル調整 */
    .sticky {
        position: sticky;
        top: 1rem;
        height: calc(100vh - 2rem);  /* ビューポートの高さから余白を引いた高さ */
        overflow-y: auto;  /* 必要に応じてスクロール可能に */
    }

    /* チャットコンテナの高さ調整 */
    .content-area {
        height: auto;
        max-height: calc(100vh - 250px); /* ヘッダーや他の要素の高さを考慮した最大高さ */
    }

    /* PCモードのチャット表示エリアの高さ調整 */
    @media (min-width: 1024px) {
        .content-area:has(iframe) {
            height: calc(100vh - 250px);
        }
    }

    /* 折りたたみ可能なヘッダーのスタイル */
    :global(.collapsible-header) {
        cursor: pointer;
        transition: background-color 0.2s ease;
    }

    :global(.collapsible-header:hover) {
        background-color: rgba(0, 0, 0, 0.05);
    }

    :global(.dark .collapsible-header:hover) {
        background-color: rgba(255, 255, 255, 0.05);
    }
    
    /* 縦書きテキスト */
    .writing-vertical-rl {
        writing-mode: vertical-rl;
        text-orientation: mixed;
        white-space: nowrap;
    }
    
    /* 折りたたまれたコンテナの高さ */
    :global(.collapsed-chat-container) {
        height: calc(61vw * 0.67);
        display: flex;
        flex-direction: column;
    }

    /* モバイル表示の横スクロール防止 */
    @media (max-width: 1023px) {
        .content-area {
            overflow-x: hidden;
        }
        
        /* 説明文のリンクが長い場合の対応 */
        .content-area a {
            word-break: break-all;
        }
    }
    
    /* ダークモードでのボタンのテキスト色を修正 */
    :global(.dark .content-area button[variant="default"]) {
        color: white;
    }
    
    :global(.dark .content-area button[variant="ghost"]:hover) {
        color: white;
    }

    /* イベント情報セクション専用のスクロールバー */
    .event-scroll::-webkit-scrollbar {
        width: 8px;
    }

    .event-scroll::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
        @apply dark:bg-white/5;
    }

    .event-scroll::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        @apply dark:bg-white/20;
    }

    .event-scroll::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.3);
        @apply dark:bg-white/30;
    }

    /* フォーカス状態の改善 */
    .event-video-button:focus {
        outline: 2px solid hsl(var(--primary));
        outline-offset: 2px;
    }

    /* ホバー時のスムーズなトランジション */
    .event-video-button {
        transition: all 0.2s ease-in-out;
    }

    .event-video-button:hover {
        transform: translateY(-1px);
    }
</style>
